import React, { useContext } from 'react';

import CardSection from '../../common/CardSection';

import './Staffing.scss';
import User from '../../../models/User';
import PagedResultSet from '../../../models/common/PagedResultSet';
import actions from 'src/actions';
import DialogList from 'src/dialogs';
import AddAdminStaffDialog from 'src/components/admin/AddStaffDialog';
import ProjectAdministrationType from 'src/models/ProjectAdministrationType';
import ProjectAdministratorStaff from 'src/models/ProjectAdministratorStaff';
import { ODataFilter, ODataFilterOperators, ODataOptions, ODataOrderByDirection } from 'src/util/ODataBuilder';
import ProjectStaff from 'src/models/ProjectStaff';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Landman from 'src/models/Landman';
// useForm removed — StaffingPage had no registered fields; handleSubmit was just
// calling setChangesSubmitted(true) with no validation needed
import useFetch from 'use-http';
import Button from 'src/components/controls/forms/redesign/FormButton';
import Filter, { FilterOrderItem } from 'src/components/common/Filter';
import CardContainer from 'src/components/common/CardContainer.redesign';
import * as _ from 'lodash';
import { ListCard } from 'src/components/common/Card.redesign';
import moment from 'moment';
import StaffingLandmanCard from './StaffingLandmanCard';
import useCollapse from 'react-collapsed';
import { IAppContextState } from 'src/reducers/AppContext';
import { AppContext } from 'src/providers/AppContextProvider';
import BillingAndPaymentCycle from 'src/models/BillingAndPaymentCycle';

import RHFLegend from 'src/components/controls/tempRHFControls/RHFLegend';
import { ContextMenuAction } from 'src/components/common/ContextMenu';
import { useDispatch } from 'react-redux';
import StaffedLandmanCard from './StaffedLandmanCard';
import LoadingSpinner from 'src/components/common/LoadingSpinner';
import ProjectBilling from 'src/models/projectSettings/ProjectBilling';
import ProjectWorkClassification from 'src/models/ProjectWorkClassification';
import ProjectDetails from 'src/models/projectSettings/ProjectDetails';
import { ITableConfig } from 'src/components/common/table';

interface IStaffingPageProps {
    hideDialog(arg0: boolean): unknown;
    clientNumber?: string;
    projectNumber?: string;
}

// Stable style objects — defined at module level so they never get recreated on re-renders
// (inline object literals defeat React.memo because prop reference changes every render)
// const STAFFING_CARD_BODY_STYLE: React.CSSProperties = { display: 'flex', marginBottom: '1rem' };
// const STAFFING_CARD_FULL_WIDTH: React.CSSProperties = { width: '100%' };
// const STAFFING_CARD_NOWRAP_FULL_WIDTH: React.CSSProperties = { whiteSpace: 'nowrap', width: '100%' };

// Styles used inside landmenCards useMemo — hoisted so they aren't recreated every time
// the memo rebuilds (which happens on every card selection change)
const LC_FLEX_ROW: React.CSSProperties = { display: 'flex' };
const LC_FULL_WIDTH: React.CSSProperties = { width: '100%' };
const LC_SPACE_BETWEEN: React.CSSProperties = { display: 'flex', justifyContent: 'space-between' };
const LC_COL_30: React.CSSProperties = { padding: '1rem', width: '30%' };
const LC_COL_40: React.CSSProperties = { padding: '1rem', width: '40%' };

// Stable module-level constants — defined outside the component so they never get
// recreated on re-renders, which would invalidate Filter's React.memo and force re-renders
const STAFFED_SORT_ITEMS: FilterOrderItem[] = [
    { group: 1, text: 'First Name', icon: 'alpha-up', orderName: 'FirstName', orderDirection: 'ascending' },
    { group: 1, text: 'First Name Desc', icon: 'alpha-down', orderName: 'FirstName', orderDirection: 'descending' },
    { group: 1, text: 'Last Name', icon: 'alpha-up', orderName: 'LastName', orderDirection: 'ascending' },
    { group: 1, text: 'Last Name Desc', icon: 'alpha-down', orderName: 'LastName', orderDirection: 'descending' },
    { group: 1, text: 'Middle Name', icon: 'alpha-up', orderName: 'MiddleName', orderDirection: 'ascending' },
    { group: 1, text: 'Middle Name Desc', icon: 'alpha-down', orderName: 'MiddleName', orderDirection: 'descending' },
    { group: 1, text: 'LMS Number', icon: 'numeric-up', orderName: 'LMSNumber', orderDirection: 'ascending' },
    { group: 1, text: 'LMS Number Desc', icon: 'numeric-down', orderName: 'LMSNumber', orderDirection: 'descending' },
];

const STAFFED_FILTER_ITEMS: FilterOrderItem[] = [
    { group: 1, text: 'First Name', orderName: 'firstName', orderDirection: 'descending' },
    { group: 1, text: 'Last Name', orderName: 'lastName', orderDirection: 'descending' },
    { group: 1, text: 'Username', orderName: 'username', orderDirection: 'descending' },
    { group: 1, text: 'Work Description', orderName: 'WorkClassification', orderDirection: 'descending' },
];

const LANDMEN_SORT_ITEMS: FilterOrderItem[] = [
    { group: 1, text: 'First Name', icon: 'alpha-up', orderName: 'FirstName', orderDirection: 'ascending' },
    { group: 1, text: 'First Name Desc', icon: 'alpha-down', orderName: 'FirstName', orderDirection: 'descending' },
    { group: 1, text: 'Last Name', icon: 'alpha-up', orderName: 'LastName', orderDirection: 'ascending' },
    { group: 1, text: 'Last Name Desc', icon: 'alpha-down', orderName: 'LastName', orderDirection: 'descending' },
    { group: 1, text: 'Middle Name', icon: 'alpha-up', orderName: 'MiddleName', orderDirection: 'ascending' },
    { group: 1, text: 'Middle Name Desc', icon: 'alpha-down', orderName: 'MiddleName', orderDirection: 'descending' },
    { group: 1, text: 'LMS Number', icon: 'numeric-up', orderName: 'LMSNumber', orderDirection: 'ascending' },
    { group: 1, text: 'LMS Number Desc', icon: 'numeric-down', orderName: 'LMSNumber', orderDirection: 'descending' },
];

const LANDMEN_FILTER_ITEMS: FilterOrderItem[] = [
    { group: 1, text: 'First Name', orderName: 'firstName', orderDirection: 'descending' },
    { group: 1, text: 'Last Name', orderName: 'lastName', orderDirection: 'descending' },
    { group: 1, text: 'Username', orderName: 'username', orderDirection: 'descending' },
    { group: 1, text: 'City', orderName: 'city', orderDirection: 'descending', type: 'date' },
    { group: 1, text: 'State', orderName: 'state', orderDirection: 'descending', type: 'date' },
    { group: 1, text: 'LMS Number', orderName: 'lmsNumber', orderDirection: 'descending', type: 'number' },
];

// ─── LandmanSelectionPanel ────────────────────────────────────────────────────
// Owns selectedLandmen state, useCollapse, and all card-selection logic.
// Extracted from StaffingPage so that clicking a search card only re-renders
// THIS component — not StaffingPage with its 18 useState + useFetch + useCollapse hooks.
interface ILandmanSelectionPanelProps {
    landmen: Landman[];
    pcapmList: User[];
    projectStaff: PagedResultSet<ProjectStaff>;
    projectAdminStaff: ProjectAdministratorStaff[];
    billingCycles: BillingAndPaymentCycle[];
    workClassifications: ProjectWorkClassification[];
    projectBilling: ProjectBilling;
    projectDetails: ProjectDetails;
    editLandmenDialog: ContextMenuAction[];
    hideDialog: () => void;
    /** Returns { isDuplicate, duplicateUserName } so the panel can show its own error UI */
    addOrUpdateProjectStaffing: (data: ProjectStaff) => Promise<{ isDuplicate: boolean; duplicateUserName?: string }>;
    updateFilter: (options: ODataOptions) => void;
    isLoading: boolean;
    isGetLandmenForbidden: boolean;
    fetchNextLandmenPage: boolean;
    incrementLandmen: () => void;
}

const LandmanSelectionPanel: React.FC<ILandmanSelectionPanelProps> = React.memo((props) => {
    const { getCollapseProps, getToggleProps, isExpanded } = useCollapse();
    const [selectedLandmen, setSelectedLandmen] = useState<Landman[]>([]);
    const [showMessage, setShowMessage] = useState(false);
    const [duplicateUser, setDuplicateUser] = useState('');

    // submitHandlersRef holds a map of landmanId → imperative submitForm() function.
    // Using a ref (not state) means registering/unregistering a card handler causes
    // zero re-renders — only the Submit button click needs to call them.
    const submitHandlersRef = React.useRef<Map<number, () => void>>(new Map());

    // Clear staging when projectStaff changes (signals a successful POST completed)
    const pendingClearRef = React.useRef(false);
    const prevProjectStaffRef = React.useRef(props.projectStaff);
    useEffect(() => {
        if (pendingClearRef.current && props.projectStaff !== prevProjectStaffRef.current) {
            setSelectedLandmen([]);
            pendingClearRef.current = false;
        }
        prevProjectStaffRef.current = props.projectStaff;
    }, [props.projectStaff]);

    const toggleSelectedEntities = useCallback((landmanId: number) => {
        setSelectedLandmen(prev => {
            const alreadySelected = prev.findIndex((ne) => ne.id === landmanId) > -1;
            if (alreadySelected) {
                return prev.filter((ne) => ne.id !== landmanId);
            } else {
                const landman = props.landmen?.find((lm) => lm.id === landmanId);
                return landman ? [...prev, landman] : prev;
            }
        });
    }, [props.landmen]);

    const selectedIds = useMemo(() => new Set(selectedLandmen.map((sl) => sl.id)), [selectedLandmen]);

    const landmenCards = useMemo(() => {
        if (!props.landmen?.length) {
            return <div>No Current Records</div>;
        }
        return props.landmen.map((landman, key) => (
            <LandmanSearchCard
                key={landman.id ?? key}
                landman={landman}
                isSelected={selectedIds.has(landman.id)}
                onToggle={toggleSelectedEntities}
            />
        ));
    }, [props.landmen, selectedIds, toggleSelectedEntities]);

    // Wrap addOrUpdateProjectStaffing to handle duplicate-user feedback locally in the panel
    const submitStaffingFormWithFeedback = useCallback(async (data: ProjectStaff) => {
        const result = await props.addOrUpdateProjectStaffing(data);
        if (result?.isDuplicate) {
            setShowMessage(true);
            setDuplicateUser(result.duplicateUserName ?? '');
            // Remove the duplicate card from staging after 3 seconds
            setTimeout(() => {
                setShowMessage(false);
                setSelectedLandmen(prev => prev.filter(lm => lm.id !== data.userId));
            }, 3000);
        }
    }, [props.addOrUpdateProjectStaffing]);

    const stagingCards = useMemo(() => {
        return selectedLandmen?.map((sl, key) => {
            const user = props.pcapmList ? props.pcapmList.find((pc) => pc.id === sl.id) : null;
            return (
                <StaffingLandmanCard
                    projectStaffInfo={props.projectStaff ? props.projectStaff?.items?.find((ps) => ps.userId === sl.id) : {}}
                    landman={sl}
                    index={key}
                    key={sl.id ?? key}
                    userList={props.pcapmList}
                    projectAdminStaff={props.projectAdminStaff}
                    submitListener={false}
                    onRegisterSubmit={(fn) => submitHandlersRef.current.set(sl.id, fn)}
                    onUnregisterSubmit={() => submitHandlersRef.current.delete(sl.id)}
                    submitStaffingForm={submitStaffingFormWithFeedback}
                    billingCycles={props.billingCycles}
                    workClassifications={props.workClassifications}
                    showDialog={props.editLandmenDialog}
                    hideDialog={props.hideDialog}
                    defaultBillRate={props.projectBilling?.dayRate}
                    defaultPayRate={user?.defaultPayRate ? user?.defaultPayRate : 0}
                    defaultPM={props.projectDetails ? props.projectDetails.projectManagerId : 0}
                    defaultPCAPM={props.projectDetails ? props.projectDetails.pcapmId : 0}
                />
            );
        });
    }, [selectedLandmen, props.pcapmList, props.projectStaff, props.projectAdminStaff,
        submitStaffingFormWithFeedback, props.billingCycles,
        props.workClassifications, props.editLandmenDialog, props.hideDialog,
        props.projectBilling, props.projectDetails]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        // Call each card's submitForm() directly — no state change, no re-renders
        submitHandlersRef.current.forEach(fn => fn());
        pendingClearRef.current = true;
    };

    return (
        <CardSection title="Selected Landmen">
            <div style={{ display: 'flex', padding: '0px', margin: '0px' }}>
                <div style={{ width: '100%', padding: '0px', margin: '0px' }}>
                    <CardContainer style={{ overflowY: 'scroll', display: selectedLandmen?.length > 0 ? undefined : 'none' }}>
                        <div className="mb-1 mt-1">
                            <div style={{ height: '20px', display: 'flex', flexDirection: 'row', justifyContent: 'space-around', margin: '0 5.5rem 0 3.5rem' }}>
                                <h3 style={{ marginRight: '5rem' }}>Staff</h3>
                                <h3 style={{ marginRight: '3rem' }}>Start Period</h3>
                                <h3 style={{ marginRight: '3rem' }}>End Period</h3>
                                <h3 style={{ marginRight: '5rem' }}>PC/APM</h3>
                                <h3 style={{ marginRight: '8rem' }}>Work Classification</h3>
                                <h3 style={{ marginRight: '8rem' }}>Project Manager</h3>
                                <h3 style={{ marginRight: '5rem' }}>Access Permissions</h3>
                            </div>
                            <form onSubmit={handleSubmit}>
                                {stagingCards}
                                <div style={{ display: 'flex', flexDirection: 'row', padding: '1rem' }}>
                                    <Button text="Submit" intent="primary" type="submit" />
                                    {showMessage && (
                                        <div style={{ color: '#ff4d4f', borderRadius: '8px', backgroundColor: 'rgba(248, 162, 164, 0.1)', padding: '0.5rem 1rem', boxShadow: '0 2px 6px rgba(139,0,0,0.5)', transition: 'opacity 0.5s ease-in-out' }}>
                                            {(!!duplicateUser ? duplicateUser : 'User') + ' is already staffed.'}
                                        </div>
                                    )}
                                </div>
                            </form>
                        </div>
                    </CardContainer>
                    <div className="filter-collapsible">
                        <div className="header" {...getToggleProps()}>
                            {isExpanded ? 'Hide' : 'Find Landmen'}
                        </div>
                        <div {...getCollapseProps()}>
                            <div className="collapsible-content">
                                <Filter
                                    sortItems={LANDMEN_SORT_ITEMS}
                                    filterItems={LANDMEN_FILTER_ITEMS}
                                    onFilterChanged={props.updateFilter}
                                />
                                {props.isLoading && (
                                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                        <h3>Loading Landmen...</h3>
                                        <LoadingSpinner />
                                    </div>
                                )}
                                {props.isGetLandmenForbidden && (
                                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                        <h3>Access denied!</h3>
                                    </div>
                                )}
                                <div>
                                    <CardContainer style={{ padding: '0px', margin: '0px' }}>{landmenCards}</CardContainer>
                                </div>
                                {props.fetchNextLandmenPage && (
                                    <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem' }}>
                                        <Button text="Load More" intent="primary" size="medium" onClick={props.incrementLandmen} />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </CardSection>
    );
});
// ─────────────────────────────────────────────────────────────────────────────

// ─── LandmanSearchCard ───────────────────────────────────────────────────────
// Extracted so React.memo can skip re-rendering unaffected cards when selection
// changes. Without this, clicking one card caused ALL 50 search-result cards to
// re-render because landmenCards useMemo depended on selectedIds.
interface ILandmanSearchCardProps {
    landman: Landman;
    isSelected: boolean;
    onToggle: (id: number) => void;
}

const LandmanSearchCard: React.FC<ILandmanSearchCardProps> = React.memo(({ landman, isSelected, onToggle }) => {
    const handleClick = useCallback(() => onToggle(landman.id), [onToggle, landman.id]);

    let currentAddress = 'N/A';
    let currentPhone = 'N/A';
    if (landman.primaryAddress) {
        const add = landman.primaryAddress;
        currentAddress = `${add.addressLine1} ${add.addressLine2}, ${add.city}, ${add.state} ${add.zipCode}`;
    }
    if (landman.primaryPhoneNumber) {
        const pho = landman.primaryPhoneNumber;
        currentPhone = `${pho.number} Ext: ${pho?.extension?.length > 0 ? pho?.extension : 'N/A'}`;
    }

    const cardClass = `small-input${isSelected ? ' selected' : ''}`;

    return (
        <ListCard className={cardClass} onClick={handleClick}>
            <div style={LC_FLEX_ROW}>
                <div className="mr-2" style={LC_FULL_WIDTH}>
                    <div style={LC_SPACE_BETWEEN}>
                        <div style={LC_COL_30}>
                            <div>Name: {`${landman.firstName} ${landman.lastName}`}</div>
                        </div>
                        <div style={LC_COL_40}>
                            <div>Address: {currentAddress}</div>
                        </div>
                        <div style={LC_COL_30}>
                            <div>Phone: {currentPhone}</div>
                        </div>
                    </div>
                </div>
            </div>
        </ListCard>
    );
});
// ─────────────────────────────────────────────────────────────────────────────

const StaffingPage = (props: IStaffingPageProps) => {
    const dispatch = useDispatch();
    const { get, post, response } = useFetch();
    const appContext = useContext<IAppContextState>(AppContext);

    // Initial Data for Staffing Page
    const [projectStaff, setProjectStaff] = useState<PagedResultSet<ProjectStaff>>(undefined);
    const [projectAdministrationTypes, setProjectAdministrationTypes] = useState<Array<ProjectAdministrationType>>([]);
    const [projectBilling, setProjectBilling] = useState<ProjectBilling>(undefined);
    const [projectDetails, setProjectDetails] = useState<ProjectDetails>(undefined);
    const [users, setUsers] = useState<PagedResultSet<User>>(undefined);
    const [staffedLandmen, setStaffedLandmen] = useState<Landman[]>(undefined);
    const [projectAdminStaff, setProjectAdminStaff] = useState<PagedResultSet<ProjectAdministratorStaff>>(undefined);

    // New States for Landman List
    const [landmen, setLandmen] = useState<Landman[]>(undefined);
    const [pcapmList, setPcapmList] = useState<User[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isLoadingStaffed, setIsLoadingStaffed] = useState<boolean>(false);
    const [isForbidden, setIsForbidden] = useState<boolean>(false);
    const [isGetLandmenForbidden, setIsGetLandmenForbidden] = useState<boolean>(false);
    const [staffComplete, setStaffComplete] = useState<boolean>(false);
    const [billingCycles, setBillingCycles] = useState<BillingAndPaymentCycle[]>([]);
    const [workClassifications, setWorkClassifications] = useState<ProjectWorkClassification[]>([]);
    const [landmanSearchDataOptions, setLandmanSearchDataOptions] = useState<ODataOptions>(undefined);
    const [staffedLandmanSearchDataOptions, setStaffedLandmanSearchDataOptions] = useState<ODataOptions>(undefined);
    const [fetchNextLandmenPage, setFetchNextLandmenPage] = useState<boolean>(true);
    const [fetchNextStaffedLandmenPage, setFetchNextStaffedLandmenPage] = useState<boolean>(true);
    // showMessage / duplicateUser moved to LandmanSelectionPanel

    useEffect(() => {
        getPCAPMUsers();
        getBillingCycles();
        getProjAdminTypes();
        getProjectBillingData();
        getProjectDetailsData();
        getWorkClassifications();
        getProjectAdministration(appContext.clientNumber, appContext.projectNumber);
        initializeLandmanSearchDataOptions();
        initializeStaffedLandmanSearchDataOptions();
        setUsers(undefined);
    }, []);

    useEffect(() => {
        if (!!landmanSearchDataOptions) {
            getLandmen(false);
        }
    }, [landmanSearchDataOptions]);

    useEffect(() => {
        if (!!staffedLandmanSearchDataOptions) {
            getProjectStaffing(appContext.clientNumber, appContext.projectNumber);
        }
    }, [staffedLandmanSearchDataOptions]);

/*     useEffect(() => {
        if (!!staffedLandmen) {
            getProjectStaffing(appContext.clientNumber, appContext.projectNumber);
        }
    }, [staffedLandmen]); */

    useEffect(() => {
        if (!!projectStaff) {
            setIsLoadingStaffed(false);
        }
    }, [projectStaff]);

    useEffect(() => {
        if (!!landmen) {
            setIsLoading(false);
        }
    }, [landmen]);

    useEffect(() => {
        if (staffComplete) {
            setStaffComplete(false);
            // Refresh staffed list — LandmanSelectionPanel clears staging via projectStaff ref watch
            initializeStaffedLandmanSearchDataOptions();
        }
    }, [staffComplete]);

    const updateLandmanSearchOptions = (filterOptions: ODataOptions) => {
        resetLandmanSearchDataOptions(filterOptions);
    };  

    const initializeStaffedLandmanSearchDataOptions = () => {
        const tableConfig: ITableConfig = {
            rowCount: 15,
            idField: 'Id',
            pageNumber: 1,
            columns: []
        };
        let newDataOptions = ODataOptions.from(tableConfig).addOrderBy('LastName', ODataOrderByDirection.Ascending);
        newDataOptions.setIsNextpage(true);
        setStaffedLandmanSearchDataOptions(newDataOptions);
    };

    const resetLandmanSearchDataOptions = (options: ODataOptions) => {
        const tableConfig: ITableConfig = {
            rowCount: 50,
            idField: 'Id',
            pageNumber: 1,
            columns: []
        };
        let newDataOptions = ODataOptions.from(tableConfig).addOrderBy('LastName', ODataOrderByDirection.Ascending);
        newDataOptions.setIsNextpage(true);
        newDataOptions.merge(options);
        setLandmanSearchDataOptions(newDataOptions);
    };

    const resetStaffedLandmanSearchDataOptions = (options: ODataOptions) => {
        const tableConfig: ITableConfig = {
            rowCount: 25,
            idField: 'Id',
            pageNumber: 1,
            columns: []
        };
        let newDataOptions = ODataOptions.from(tableConfig).addOrderBy('LastName', ODataOrderByDirection.Ascending);
        newDataOptions.setIsNextpage(true);
        newDataOptions.merge(options);
        setStaffedLandmanSearchDataOptions(newDataOptions);
    };

    const initializeLandmanSearchDataOptions = () => {
        const tableConfig: ITableConfig = {
            rowCount: 50,
            idField: 'Id',
            pageNumber: 1,
            columns: []
        };
        let newDataOptions = ODataOptions.from(tableConfig).addOrderBy('LastName', ODataOrderByDirection.Ascending);
        newDataOptions.setIsNextpage(true);
        setLandmanSearchDataOptions(newDataOptions);
    };

    const getLandmen = async (staffed: boolean) => {
        if (!!staffed) {
            setIsLoadingStaffed(true);
        } else {
            setIsLoading(true);
        }
        const queryString = !!landmanSearchDataOptions ? landmanSearchDataOptions.toString() : '';
        let data = await get(`v1.0/landmen/client/${appContext.clientNumber}/project/${appContext.projectNumber}${queryString}`);

        if (response.ok) {
            if (staffed) {
                if (!!staffedLandmanSearchDataOptions && (staffedLandmanSearchDataOptions?.getPageNumber() > 1)) {
                    let tempResult = _.cloneDeep(staffedLandmen);
                    tempResult = [...tempResult].concat([...data.items]);
                    setStaffedLandmen(tempResult);
                } else {
                    setStaffedLandmen(data.items);
                }
                setFetchNextStaffedLandmenPage(data.pageNumber * data.pageSize < data.totalRecords);
            } else {
                if (!!landmanSearchDataOptions && (landmanSearchDataOptions?.getPageNumber() > 1)) {
                    let tempResult = _.cloneDeep(landmen);
                    tempResult = [...tempResult].concat([...data.items]);
                    setLandmen(tempResult);
                } else {
                    setLandmen(data.items);
                }
                setFetchNextLandmenPage(data.pageNumber * data.pageSize < data.totalRecords);
            }
        } else {
            if (response.status === 403 ) {
                setIsGetLandmenForbidden(true);
                setIsLoading(false);
            }
            console.log('Error getting Landmen data:', response);
        }
    };

    const getProjectStaffing = async (clientId: number, projectId: number) => {
        setIsLoadingStaffed(true);
        const queryString = !!staffedLandmanSearchDataOptions ? staffedLandmanSearchDataOptions.toString() : '';
        let data: PagedResultSet<ProjectStaff> = await get(`v1.0/clients/${clientId}/projects/${projectId}/projectstaff/staffing${queryString}`);

        if (response.ok) {
            if (!!staffedLandmanSearchDataOptions && (staffedLandmanSearchDataOptions?.getPageNumber() > 1)) {
                let tempResult = _.cloneDeep(projectStaff.items);
                tempResult = [...tempResult].concat([...data.items]);
                const newProjectStaff = new PagedResultSet<ProjectStaff>();
                newProjectStaff.items = [...tempResult];
                setProjectStaff(newProjectStaff);
            } else {
                setProjectStaff(data);
            }
            setFetchNextStaffedLandmenPage(data.pageNumber * data.pageSize < data.totalRecords);
        } else {
            if (response.status === 403 ) {
                setIsLoadingStaffed(false);
                setIsForbidden(true);
            }
            console.log('Unable to retrieve Staffing Data.');
        }
    };

    const getProjectAdministration = async (clientId: number, projectId: number) => {
        let data = await get(`v1.0/clients/${clientId}/projects/${projectId}/projectstaff/administration`);

        if (response.ok) {
            setProjectAdminStaff(data);
        } else {
            console.log('Unable to retrieve Staffing Data.');
        }
    };

    const getProjAdminTypes = async () => {
        let data = await get(`v1.0/users/pm-types`);

        if (response.ok) {
            setProjectAdministrationTypes(data);
        } else {
            console.log('Unable to retrieve Project Admin Types.');
        }
    };

    const getPCAPMUsers = async () => {
        let data: PagedResultSet<User> = await get(`v1.0/users`);

        if (response.ok) {
            setPcapmList(data.items);
        } else {
            console.log('Unable to retrieve PCAPM List.');
        }
    };

    const getBillingCycles = async () => {
        let data = await get(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/projectstaff/billing-cycles`);

        if (response.ok) {
            setBillingCycles(data);
        }
    };

    const getWorkClassifications = async () => {
        let data = await get(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/projectstaff/work-classifications`);

        if (response.ok) {
            setWorkClassifications(data);
        }
    };

    const getProjectBillingData = async () => {
        const data: ProjectBilling = await get(`v1.0/clients/${appContext.clientNumber}/Projects/${appContext.projectNumber}/project-billing`);

        if (response.ok) {
            setProjectBilling(data);
        } else {
            console.log('Unable to get Project Billing Data');
        }
    };

    const getProjectDetailsData = async () => {
        const data = await get(`v1.0/clients/${appContext.clientNumber}/Projects/${appContext.projectNumber}/project-details`);

        if (response.ok) {
            setProjectDetails(data);
        } else {
            console.log('Unable to get Project Details data');
        }
    };

    const hideDialog = useCallback(() => actions.layout.hideDialog(), []);
    const getUsersByOptions = (options: ODataOptions) => actions.user.getUsersByOptions(options);
    const initUsers = () => actions.user.initUsers();
    const addUserToStaff = (clientId: number, projectId: number, staffInfo: ProjectAdministratorStaff) => {
        actions.user.addProjectAdministrationStaff(clientId, projectId, staffInfo);
    };

    const formatProjectStaffData = (data: ProjectStaff) => {
        data.id = data.id ? parseInt(data.id + '', 10) : 0;
        data.projectId = appContext.projectNumber;
        data.userId = data.userId ? parseInt(data.userId + '', 10) : 0;
        data.projectAdministratorId = data.projectAdministratorId ? parseInt(data.projectAdministratorId + '', 10) : null;
        data.projectManagerId = data.projectManagerId ? parseInt(data.projectManagerId + '', 10) : null;
        data.pcapmId = data.pcapmId ? parseInt(data.pcapmId + '', 10) : null;
        data.workClassificationId = data.workClassificationId ? parseInt(data.workClassificationId + '', 10) : 0;
        data.startBillingCycleId = data.startBillingCycleId
            ? parseInt(data.startBillingCycleId + '', 10)
            : data.startBillingCycle
            ? parseInt(data.startBillingCycle.id + '', 10)
            : null;
        data.endBillingCycleId = data.endBillingCycleId
            ? parseInt(data.endBillingCycleId + '', 10)
            : data.endBillingCycle
            ? parseInt(data.endBillingCycle.id + '', 10)
            : null;
        data.startBillingCycle = billingCycles ? billingCycles.find((bc: BillingAndPaymentCycle) => bc.id === data.startBillingCycleId) : null;
        data.endBillingCycle = billingCycles ? billingCycles.find((bc: BillingAndPaymentCycle) => bc.id === data.endBillingCycleId) : null;
        data.overrideBillRate = data.overrideBillRate ? parseFloat(data.overrideBillRate + '') : 0;
        data.overridePayRate = data.overridePayRate ? parseFloat(data.overridePayRate + '') : 0;
        data.ownershipAccess = !!data.ownershipAccess ? data.ownershipAccess : null;
        data.reportAccess = !!data.reportAccess ? data.reportAccess : null;
        data.contractAccess = !!data.contractAccess ? data.contractAccess : null;
        data.gisAccess = !!data.gisAccess ? data.gisAccess : null;
        data.startDate = data.startDate ? (moment(data.startDate).format('YYYY-MM-DD') as unknown as Date) : null;
        data.endDate = data.endDate ? (moment(data.endDate).format('YYYY-MM-DD') as unknown as Date) : null;

        return data;
    };

    const addOrUpdateProjectStaffing = useCallback(async (data: ProjectStaff): Promise<{ isDuplicate: boolean; duplicateUserName?: string }> => {
        let newData = formatProjectStaffData(data);

        console.log('after sanitization ', newData);

        if (!newData.id || newData.id === 0) {
            console.log('no id found, posting new staff', newData);
            await post(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/projectstaff/staffing`, newData);
            setStaffComplete(true);
            return { isDuplicate: false };
        } else {
            // This landman is already staffed — return info for the panel to display
            const projectStaffer = projectStaff.items.find((lm) => lm.id === data.id);
            const duplicateUserName = projectStaffer
                ? `${projectStaffer.user?.firstName} ${projectStaffer.user?.lastName}`
                : undefined;
            return { isDuplicate: true, duplicateUserName };
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [post, projectStaff, billingCycles, appContext.clientNumber, appContext.projectNumber]);

    const updateFilter = useCallback((options: ODataOptions) => {
        if (!!landmanSearchDataOptions) {
            updateLandmanSearchOptions(options);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [landmanSearchDataOptions]);

    const findByNameOrAlias = (value: string): number | undefined => {
        const workClassificationObject = workClassifications.find(r => r.name === value || r.alias === value);
        return workClassificationObject.id;
    };

    const getWorkClassificationCode = (wcValue: string) => {
        return findByNameOrAlias(wcValue);
    };

    const updateWorkClassification = (arr: ODataFilter[]): ODataFilter[] => {
        return arr.map(filter => {
          const filterString = filter.toString();
          if (filterString.includes('WorkClassification')) {
            const wcValue = filterString.split(',')[1];
            return new ODataFilter(
              'where',
              ODataFilterOperators.Equals,
              'WorkClassification,' + getWorkClassificationCode(wcValue)
            );
          }
          return filter;
        });
    };

    const updateStaffedFilter = useCallback((options: ODataOptions) => {
        if (options && options.hasFilters()) {
            const originalFilters = _.cloneDeep(options.getFilters());
            const updatedFilters = updateWorkClassification(originalFilters);
            options.setFilters(updatedFilters);
        }

        if (!!staffedLandmanSearchDataOptions) {
            const orderByValue = options.getOrderByProperty();
            const existingOrderByValue = staffedLandmanSearchDataOptions.getOrderByProperty();
            if (options.hasFilters() || orderByValue || existingOrderByValue || staffedLandmanSearchDataOptions.hasFilters()) {
                resetStaffedLandmanSearchDataOptions(options);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [staffedLandmanSearchDataOptions, workClassifications]);

    const editLandmenDialog: ContextMenuAction[] = useMemo(() => [
        { text: 'Manage Landmen', action: () => dispatch(actions.layout.showDialog(DialogList.STAFFING_LANDMEN)) },
    ], [dispatch]);

    const updateAfterDestaffed = useCallback((data: boolean) => {
        if (!!data) {
            getProjectStaffing(appContext.clientNumber, appContext.projectNumber);
            getLandmen(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appContext.clientNumber, appContext.projectNumber]);

    const updateAfterNewData = useCallback((data: boolean) => {
        if (!!data) {
            // Don't set projectStaff to undefined — keep the existing list visible
            // while the refresh is in flight to avoid the blank-list flash
            getProjectStaffing(appContext.clientNumber, appContext.projectNumber);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appContext.clientNumber, appContext.projectNumber]);

    const incrementStaffedLandmen = () => {
        if (!!fetchNextStaffedLandmenPage) {
            let newSearchOptions = _.cloneDeep(staffedLandmanSearchDataOptions);
            newSearchOptions.incrementPageNumber();
            setStaffedLandmanSearchDataOptions(newSearchOptions);
        }
    };

    const incrementLandmen = () => {
        if (!!fetchNextLandmenPage) {
            let newSearchOptions = _.cloneDeep(landmanSearchDataOptions);
            newSearchOptions.incrementPageNumber();
            setLandmanSearchDataOptions(newSearchOptions);
        }
    };

    const staffedLandmanCards = useMemo(() => {
        if (isLoadingStaffed || !projectStaff?.items?.length) {
            return <></>;
        }
        return projectStaff.items.map((ps, key) => {
            const landman: Landman = {
                id: ps.id, firstName: ps.user.firstName, lastName: ps.user.lastName,
                managerId: ps.projectManagerId, personDetailsId: null, username: ps.user.username,
                phoneNumberList: null, employees: null, performanceComments: null, primaryPhoneNumber: null,
            };
            return (
                <StaffedLandmanCard
                    projectStaffInfo={ps}
                    landman={landman}
                    index={key}
                    key={ps.id ?? key}
                    pcapmList={pcapmList}
                    projectAdminStaff={projectAdminStaff?.items}
                    billingCycles={billingCycles}
                    workClassifications={workClassifications}
                    showDialog={editLandmenDialog}
                    hideDialog={hideDialog}
                    deStaffedListener={updateAfterDestaffed}
                    updateCardListener={updateAfterNewData}
                />
            );
        });
    }, [projectStaff, isLoadingStaffed, pcapmList, projectAdminStaff,
        billingCycles, workClassifications, editLandmenDialog,
        hideDialog, updateAfterDestaffed, updateAfterNewData]);

    return (
        <>
            <RHFLegend title="Project Staffing" />
            <Filter
                sortItems={STAFFED_SORT_ITEMS}
                filterItems={STAFFED_FILTER_ITEMS}
                onFilterChanged={updateStaffedFilter}
            />
            {isLoadingStaffed && (
                <div
                    style={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}
                >
                    <h3>Loading Staffed Landmen...</h3>
                    <LoadingSpinner />
                </div>
            )}
            {isForbidden && (
                <div
                    style={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}
                >
                    <h3>Access denied!</h3>
                </div>
            )}
            <CardSection title="Project's Current Staff">
                <CardContainer>
                    {projectStaff?.items?.length === 0 && (
                        <div style={{ display: 'flex', justifyContent: 'center', height: '50px' }}>
                            <p>No employees have been staffed to this project!</p>
                        </div>
                    )}
                    {projectStaff?.items?.length > 0 && (
                        <div style={{margin: '0px'}} className="mb-1 mt-1">
                            {/*tslint:disable-next-line:max-line-length*/}
                            <div style={{height: '20px', display: 'flex', flexDirection: 'row', verticalAlign: 'middle', alignContent: 'left', justifyContent: 'left', margin: '0 0 0 3.5rem' }}>
                                <h3 style={{ marginLeft: '1rem', marginRight: '12rem' }}>Name</h3>
                                <h3 style={{ marginRight: '8rem' }}>Start Period</h3>
                                <h3 style={{ marginRight: '10rem' }}>End Period</h3>
                                <h3 style={{ marginRight: '20rem' }}>Work Classification</h3>
                                <h3 style={{ marginRight: '0rem' }}>Access Permissions</h3>
                            </div>
                            <div id={'projectStaffList'}>
                                {staffedLandmanCards}
                            </div>
                            {fetchNextStaffedLandmenPage && (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem' }}>
                                    <Button
                                        text="Load More"
                                        intent="primary"
                                        size="medium"
                                        onClick={incrementStaffedLandmen}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </CardContainer>
            </CardSection>

            <LandmanSelectionPanel
                landmen={landmen}
                pcapmList={pcapmList}
                projectStaff={projectStaff}
                projectAdminStaff={projectAdminStaff?.items}
                billingCycles={billingCycles}
                workClassifications={workClassifications}
                projectBilling={projectBilling}
                projectDetails={projectDetails}
                editLandmenDialog={editLandmenDialog}
                hideDialog={hideDialog}
                addOrUpdateProjectStaffing={addOrUpdateProjectStaffing}
                updateFilter={updateFilter}
                isLoading={isLoading}
                isGetLandmenForbidden={isGetLandmenForbidden}
                fetchNextLandmenPage={fetchNextLandmenPage}
                incrementLandmen={incrementLandmen}
            />
            <AddAdminStaffDialog
                clientNumber={appContext.clientNumber}
                projectNumber={appContext.projectNumber}
                itemType="Client"
                dialogName={DialogList.ADD_STAFF}
                getProjAdminTypes={getProjAdminTypes}
                projectAdministrationTypes={projectAdministrationTypes}
                getUsersByOptions={getUsersByOptions}
                onCloseDialog={initUsers}
                users={users}
                addUserToStaff={addUserToStaff}
                hideDialog={hideDialog}
            />
        </>
    );
};

export default StaffingPage;



// WEBPACK FOOTER //
// ./src/components/project/sections/StaffingPage.tsx